import * as React from 'react';
import axios from 'axios';
import Keycloak from 'keycloak-js';

import { App } from './App';
import { AuthContext, IAuth, IAuthUserdata } from './AuthContext';

interface IProps {
  children?: React.ReactNode;
}

// Refresh the token when it has under this many seconds left, checked on this interval.
const REFRESH_CHECK_INTERVAL_MS = 30_000;
const REFRESH_MIN_VALIDITY_S = 70;

/**
 * Auth wires the app to Keycloak. On mount it redirects unauthenticated users to
 * Keycloak's hosted login (which also owns forgot/reset password). Once
 * authenticated it fetches /me for the user's identity + role-derived rights,
 * keeps the access token fresh, and exposes signout / account-console helpers.
 * Children render only once authenticated.
 */
const Auth = (props: IProps) => {
  const keycloakRef = React.useRef<Keycloak | null>(null);
  const refreshTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const [ready, setReady] = React.useState(false);
  const [userData, setUserData] = React.useState<IAuthUserdata>({
    id: null, name: null, email: null, roles: [], rights: [], access_token: ''
  });

  const appUrl = () => `${window.location.origin}${window.location.pathname}`;

  const fetchMe = () => {
    axios.get(`${App.config.apiURL}me`)
      .then((res) => {
        setUserData({
          id: res.data.id,
          name: res.data.name,
          email: res.data.email,
          roles: res.data.roles ?? [],
          rights: res.data.rights ?? [],
          access_token: ''
        });
        setReady(true);
        document.getElementById('apploader')?.remove();
      })
      .catch((err) => {
        console.error('/me failed', err);
      });
  };

  React.useEffect(() => {
    const kc = new Keycloak({
      url: App.config.keycloakURL,
      realm: App.config.keycloakRealm,
      clientId: App.config.keycloakClient
    });
    keycloakRef.current = kc;

    kc.init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false })
      .then((authenticated) => {
        if (!authenticated) return; // Keycloak is redirecting to its login page.

        App.accessToken = kc.token ?? null;
        App.onUnauthorized = () => signout();

        // Reject users with no role for this application.
        const parsed = kc.tokenParsed as { resource_access?: Record<string, { roles?: string[] }> } | undefined;
        const roles = parsed?.resource_access?.[App.config.keycloakClient]?.roles ?? [];
        if (roles.length === 0) {
          console.error('User has no roles for this application — signing out.');
          kc.logout({ redirectUri: appUrl() });
          return;
        }

        fetchMe();

        // Keep the token fresh.
        refreshTimerRef.current = setInterval(() => {
          kc.updateToken(REFRESH_MIN_VALIDITY_S)
            .then((refreshed) => { if (refreshed) App.accessToken = kc.token ?? null; })
            .catch(() => { kc.login(); });
        }, REFRESH_CHECK_INTERVAL_MS);
      })
      .catch((err) => {
        console.error('Keycloak init failed', err);
      });

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signout = () => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    App.accessToken = null;
    keycloakRef.current?.logout({ redirectUri: appUrl() });
  };

  const accountManagement = () => {
    const url = keycloakRef.current?.createAccountUrl();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleHasRight = (right: string | boolean) => {
    if (right === true) return true;
    if (!userData.rights) return false;
    return userData.rights.includes(right as string);
  };

  const value: IAuth = {
    ...userData,
    signout,
    accountManagement,
    hasRight: handleHasRight
  };

  // Render nothing until Keycloak has authenticated and /me has loaded; the
  // hosted login page takes over for unauthenticated users.
  if (!ready) return null;

  return (
    <AuthContext.Provider value={value}>
      {props.children}
    </AuthContext.Provider>
  );
}

export { Auth }
